import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchEvents, createEvent, fetchPopularEvents, fetchOpenContributionEvents, type OpenContributionEvent } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { LanguageSwitcher } from '../components/ui/LanguageSwitcher';
import { LikeButton } from '../components/ui/LikeButton';
import { LoginRequiredModal } from '../components/auth/LoginRequiredModal';
import { BadgeList } from '../components/ui/BadgeIcon';
import { RoutePreviewSvg } from '../components/ui/RoutePreviewSvg';
import type { Event, PopularEvent, BadgeType } from '../types';

export function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  // const [events, setEvents] = useState<Event[]>([]); // Uncomment when Live Events is re-enabled
  const [finishedEvents, setFinishedEvents] = useState<Event[]>([]);
  const [popularEvents, setPopularEvents] = useState<PopularEvent[]>([]);
  const [openContributionEvents, setOpenContributionEvents] = useState<OpenContributionEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  // const [eventCode, setEventCode] = useState(''); // Uncomment when Join Event is re-enabled
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showOpenEventLoginModal, setShowOpenEventLoginModal] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    }
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      const [allEventsData, popularData, openContributionData] = await Promise.all([
        fetchEvents(),
        fetchPopularEvents(5),
        fetchOpenContributionEvents(5),
      ]);
      // setEvents(allEventsData.filter(e => e.status !== 'finished')); // Uncomment when Live Events is re-enabled
      setFinishedEvents(allEventsData.filter(e => e.status === 'finished' && !e.isOpenContribution));
      setPopularEvents(popularData.filter(e => e.likeCount > 0));
      setOpenContributionEvents(openContributionData.events);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setIsLoading(false);
    }
  }

  // Get location name based on current language
  function getLocationName(event: Event | PopularEvent | OpenContributionEvent): string | null {
    if (i18n.language === 'ko') {
      return event.locationNameKo || event.locationName || null;
    }
    return event.locationName || null;
  }

  function formatDuration(startedAt?: number, finishedAt?: number): string {
    if (!startedAt || !finishedAt) return '';
    const durationMs = Number(finishedAt) - Number(startedAt);
    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  function formatDate(timestamp?: number): string {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Uncomment when Join Event is re-enabled
  // function handleJoinByCode() {
  //   if (eventCode.trim()) {
  //     const event = events.find((e) => e.code === eventCode.toUpperCase());
  //     if (event) {
  //       navigate(`/event/${event.id}`);
  //     } else {
  //       alert(t('home.eventNotFound'));
  //     }
  //   }
  // }

  return (
    <div key={i18n.language} className="min-h-screen overflow-y-auto bg-[#0a0a0a]">
      {/* Blurred cycling background image */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-110 blur-md"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&w=2000&q=80)',
          }}
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/85 to-black/95" />
        {/* Subtle color accent overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#e10600]/10 via-transparent to-[#00d2be]/10" />
        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#e10600] via-[#00d2be] to-[#ff8700]" />
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Logo with racing accent */}
            <button
              onClick={() => window.location.reload()}
              className="relative cursor-pointer"
            >
              <div className="absolute -inset-2 bg-gradient-to-r from-[#e10600] to-[#ff8700] blur-xl opacity-30" />
              <h1 className="relative text-2xl sm:text-4xl font-black text-white font-display" style={{ letterSpacing: 2 }}>
                PELO<span className="text-[#e10600]">1</span>
              </h1>
            </button>
            <div className="h-6 sm:h-8 w-px bg-white/20" />
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/40 font-mono leading-tight">
              {t('home.tagline').split(' ').map((word, i) => (
                <div key={i}>{word}</div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {isAuthenticated ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                >
                  {user?.profileImage ? (
                    <img src={user.profileImage} alt="" className="w-7 h-7 rounded-full ring-2 ring-[#e10600]" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#e10600] to-[#ff8700] flex items-center justify-center text-xs font-bold text-white">
                      {user?.nickname?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-semibold text-white">{user?.nickname}</span>
                  {user?.badges && user.badges.length > 0 && (
                    <BadgeList badges={user.badges.map(b => b.type as BadgeType)} size="sm" />
                  )}
                  <svg className={`w-4 h-4 text-white/50 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-48 rounded-xl bg-[#1a1a1a] border border-white/10 shadow-xl overflow-hidden z-50">
                    <Link
                      to="/profile"
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {t('nav.profile')}
                    </Link>
                    <Link
                      to="/activities"
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      {t('nav.myActivities')}
                    </Link>
                    <div className="border-t border-white/5" />
                    <button
                      onClick={() => { logout(); setShowProfileMenu(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-white/50 hover:text-[#e10600] hover:bg-[#e10600]/5 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* 모바일: 통합 아이콘 버튼 하나 */}
                <Link to="/login" className="sm:hidden p-2.5 rounded-lg text-white/70 hover:text-white border border-white/10 hover:border-white/20 transition-all">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </Link>
                {/* 데스크탑: 로그인 버튼 */}
                <Link to="/login" className="hidden sm:block relative group px-5 py-2.5 rounded-lg text-sm font-bold text-white overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#e10600] to-[#ff8700] group-hover:scale-105 transition-transform" />
                  <span className="relative">{t('nav.login')}</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
              {t('home.heroTitle')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e10600] to-[#ff8700]">{t('home.heroHighlight')}</span>
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              {t('home.heroSubtitle')}
            </p>
          </div>

          {/* Action Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Join Event - Hidden for now, uncomment to re-enable
            <div className="group relative rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-[#1a1a1a]/80" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#00d2be]/30 to-transparent" />
              <div className="absolute inset-0 border border-[#00d2be]/40 rounded-2xl" />
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-[#00d2be]/30 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00d2be]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('home.watchLive')}</h3>
                <p className="text-sm text-white/70 mb-4">{t('home.watchLiveDesc')}</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={eventCode}
                    onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    maxLength={6}
                    className="flex-1 min-w-0 px-4 py-3 rounded-lg bg-black/50 border border-white/10 font-mono text-lg tracking-widest text-center text-white placeholder-white/30 focus:border-[#00d2be] focus:outline-none transition-colors"
                  />
                  <button
                    onClick={handleJoinByCode}
                    disabled={eventCode.length !== 6}
                    className="shrink-0 px-4 py-3 rounded-lg bg-[#00d2be] text-black font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#00e5d0] transition-colors"
                  >
                    GO
                  </button>
                </div>
              </div>
            </div>
            */}

            {/* Create Event - Coming Soon */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer opacity-60" onClick={() => setShowComingSoonModal(true)}>
              <div className="absolute inset-0 bg-[#1a1a1a]/80" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#e10600]/30 to-transparent" />
              <div className="absolute inset-0 border border-[#e10600]/40 rounded-2xl" />
              {/* Coming Soon Badge */}
              <div className="absolute top-3 right-3 px-2 py-1 bg-[#e10600]/80 rounded text-xs font-bold text-white uppercase tracking-wider">
                {t('home.comingSoon')}
              </div>
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-[#e10600]/30 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#e10600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('home.hostEvent')}</h3>
                <p className="text-sm text-white/70 mb-4">{t('home.hostEventDesc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-[#e10600]/50">{t('home.startRacing')} →</span>
                  <div className="w-8 h-8 rounded-full bg-[#e10600]/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-[#e10600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* FIT Replay */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer" onClick={() => navigate('/fit-replay/create')}>
              <div className="absolute inset-0 bg-[#1a1a1a]/80" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0082FA]/30 to-transparent group-hover:from-[#0082FA]/40 transition-colors" />
              <div className="absolute inset-0 border border-[#0082FA]/40 rounded-2xl group-hover:border-[#0082FA]/60 transition-colors" />
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-[#0082FA]/30 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#0082FA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('home.fitReplay')}</h3>
                <p className="text-sm text-white/70 mb-4">{t('home.fitReplayDesc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-[#0082FA]">{t('home.uploadFiles')} &rarr;</span>
                  <div className="w-8 h-8 rounded-full bg-[#0082FA]/20 flex items-center justify-center group-hover:bg-[#0082FA]/30 transition-colors">
                    <svg className="w-4 h-4 text-[#0082FA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Strava Replay */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer" onClick={() => navigate('/strava-replay/create')}>
              <div className="absolute inset-0 bg-[#1a1a1a]/80" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#fc4c02]/30 to-transparent group-hover:from-[#fc4c02]/40 transition-colors" />
              <div className="absolute inset-0 border border-[#fc4c02]/40 rounded-2xl group-hover:border-[#fc4c02]/60 transition-colors" />
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-[#fc4c02]/30 flex items-center justify-center mb-4">
                  {/* Strava logo */}
                  <svg className="w-6 h-6 text-[#fc4c02]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('stravaReplay.importFromStrava')}</h3>
                <p className="text-sm text-white/70 mb-4">{t('stravaReplay.importFromStravaDesc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-[#fc4c02]">Strava &rarr;</span>
                  <div className="w-8 h-8 rounded-full bg-[#fc4c02]/20 flex items-center justify-center group-hover:bg-[#fc4c02]/30 transition-colors">
                    <svg className="w-4 h-4 text-[#fc4c02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Open Event */}
            <div className="group relative rounded-2xl overflow-hidden cursor-pointer" onClick={() => {
              if (isAuthenticated) {
                navigate('/open-event/create');
              } else {
                setShowOpenEventLoginModal(true);
              }
            }}>
              <div className="absolute inset-0 bg-[#1a1a1a]/80" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#00D2BE]/30 to-transparent group-hover:from-[#00D2BE]/40 transition-colors" />
              <div className="absolute inset-0 border border-[#00D2BE]/40 rounded-2xl group-hover:border-[#00D2BE]/60 transition-colors" />
              <div className="relative p-6">
                <div className="w-12 h-12 rounded-xl bg-[#00D2BE]/30 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-[#00D2BE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('openEvent.create')}</h3>
                <p className="text-sm text-white/70 mb-4">{t('openEvent.createDesc')}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-[#00D2BE]">{t('openEvent.createShort')} &rarr;</span>
                  <div className="w-8 h-8 rounded-full bg-[#00D2BE]/20 flex items-center justify-center group-hover:bg-[#00D2BE]/30 transition-colors">
                    <svg className="w-4 h-4 text-[#00D2BE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="relative z-10 px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Live Events - Hidden for now, uncomment to re-enable
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 rounded-full bg-[#00d2be] animate-pulse" />
              <h3 className="text-2xl font-bold text-white">{t('home.liveNow')}</h3>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 border-4 border-[#e10600] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-2xl border border-white/20 bg-[#1a1a1a]/60 p-12 text-center">
                <p className="text-white/60">{t('home.noActiveEvents')}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {events.map((event) => (
                  <Link
                    key={event.id}
                    to={`/event/${event.id}`}
                    className="group relative rounded-xl overflow-hidden text-left transition-all hover:scale-[1.01]"
                  >
                    <div className="absolute inset-0 bg-[#1a1a1a]/70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent group-hover:from-white/15 transition-colors" />
                    <div className="absolute inset-0 border border-white/20 rounded-xl group-hover:border-white/30 transition-colors" />
                    {event.status === 'active' && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d2be] to-[#00d2be]/50" />
                    )}
                    <div className="relative p-4 sm:p-5 pl-5 sm:pl-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4 flex-1 min-w-0">
                          {event.routePreview && (
                            <div className="hidden sm:block shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={56}
                                height={42}
                                strokeColor={event.status === 'active' ? '#00d2be' : '#ff8700'}
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                              <span className="text-base sm:text-lg font-bold text-white truncate">{event.name}</span>
                              {event.status === 'active' && (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#00d2be]/20 text-[#00d2be] text-[10px] sm:text-xs font-bold uppercase tracking-wider animate-pulse shrink-0">
                                  LIVE
                                </span>
                              )}
                              {event.status === 'pending' && (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#ff8700]/20 text-[#ff8700] text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
                                  PENDING
                                </span>
                              )}
                            </div>
                            <div className="hidden sm:flex items-center gap-4 text-sm text-white/60">
                              <span className="font-mono">{event.code}</span>
                              <span>{event.riderCount || 0} {t('home.riders')}</span>
                              {(event.course?.distance ?? event.totalDistance) != null && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                  </svg>
                                  {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                                </span>
                              )}
                              {getLocationName(event) && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate max-w-32">{getLocationName(event)}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors shrink-0">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                        <div className="flex sm:hidden items-center gap-3">
                          {event.routePreview && (
                            <div className="shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={48}
                                height={36}
                                strokeColor={event.status === 'active' ? '#00d2be' : '#ff8700'}
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-xs text-white/60 flex-wrap">
                            <span className="font-mono">{event.code}</span>
                            <span>{event.riderCount || 0} {t('home.riders')}</span>
                            {(event.course?.distance ?? event.totalDistance) != null && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                              </span>
                            )}
                            {getLocationName(event) && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <span className="truncate max-w-24">{getLocationName(event)}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          */}

          {/* Open Contribution Events */}
          {openContributionEvents.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <svg className="w-5 h-5 text-[#00D2BE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-2xl font-bold text-white">{t('contribution.openEvents')}</h3>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {openContributionEvents.slice(0, 4).map((event) => (
                  <Link
                    key={event.id}
                    to={`/event/${event.id}`}
                    className="group relative rounded-xl overflow-hidden text-left transition-all hover:scale-[1.01]"
                  >
                    <div className="absolute inset-0 bg-[#1a1a1a]/80" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#00D2BE]/20 to-transparent group-hover:from-[#00D2BE]/30 transition-colors" />
                    <div className="absolute inset-0 border border-[#00D2BE]/30 rounded-xl group-hover:border-[#00D2BE]/50 transition-colors" />
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00D2BE] to-[#00D2BE]/50" />
                    <div className="relative p-4">
                      <div className="flex items-start gap-3">
                        {/* RoutePreview */}
                        {event.routePreview && (
                          <div className="shrink-0 rounded-lg bg-white/5 p-1">
                            <RoutePreviewSvg
                              preview={event.routePreview}
                              width={56}
                              height={42}
                              strokeColor="#00D2BE"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-white truncate">{event.name}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0 ${
                              event.contributionMode === 'time_trial'
                                ? 'bg-[#00FF87]/20 text-[#00FF87]'
                                : 'bg-[#00D2BE]/20 text-[#00D2BE]'
                            }`}>
                              {event.contributionMode === 'time_trial' ? 'TIME TRIAL' : 'REPLAY'}
                            </span>
                          </div>
                          {getLocationName(event) && (
                            <p className="text-xs text-white/50 truncate mb-2">{getLocationName(event)}</p>
                          )}
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-white/50">
                              {event.currentRiderCount}/{event.maxRiders} {t('home.riders')}
                            </span>
                            <span className="text-[#00D2BE] font-medium">
                              {event.spotsRemaining} {t('contribution.spotsRemaining')}
                            </span>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-[#00D2BE] to-[#00FF87]"
                              style={{ width: `${(event.currentRiderCount / event.maxRiders) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Popular Replays */}
          {popularEvents.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <svg className="w-5 h-5 text-[#e10600]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <h3 className="text-2xl font-bold text-white">{t('home.popularReplays')}</h3>
              </div>

              <div className="grid gap-4">
                {popularEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/event/${event.id}`}
                    className="group relative rounded-xl overflow-hidden text-left transition-all hover:scale-[1.01]"
                  >
                    <div className="absolute inset-0 bg-[#1a1a1a]/80" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#e10600]/20 to-transparent group-hover:from-[#e10600]/30 transition-colors" />
                    <div className="absolute inset-0 border border-[#e10600]/30 rounded-xl group-hover:border-[#e10600]/50 transition-colors" />
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#e10600] to-[#e10600]/50" />
                    <div className="relative p-4 sm:p-5 pl-5 sm:pl-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        {/* 1행: 제목 + 버튼 */}
                        <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4 flex-1 min-w-0">
                          {/* RoutePreview - 데스크탑에서만 이 위치 */}
                          {event.routePreview && (
                            <div className="hidden sm:block shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={56}
                                height={42}
                                strokeColor={event.type === 'fit_replay' ? '#0082FA' : '#e10600'}
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                              <span className="text-base sm:text-lg font-bold text-white truncate">{event.name}</span>
                              {event.type === 'fit_replay' ? (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#0082FA]/20 text-[#0082FA] text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
                                  FIT
                                </span>
                              ) : (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#00d2be]/20 text-[#00d2be] text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
                                  REPLAY
                                </span>
                              )}
                            </div>
                            {/* 메타 정보 - 데스크탑에서만 이 위치 */}
                            <div className="hidden sm:flex items-center gap-4 text-sm text-white/60">
                              <span>{formatDate(event.finishedAt)}</span>
                              <span>{event.riderCount || 0} {t('home.riders')}</span>
                              {(event.course?.distance ?? event.totalDistance) != null && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                  </svg>
                                  {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                                </span>
                              )}
                              {event.startedAt && event.finishedAt && (
                                <span>{formatDuration(event.startedAt, event.finishedAt)}</span>
                              )}
                              {getLocationName(event) && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate max-w-32">{getLocationName(event)}</span>
                                </span>
                              )}
                              {(event.viewCount ?? 0) > 0 && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  {event.viewCount}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* 버튼들 */}
                          <div className="flex items-center gap-2 shrink-0">
                            <LikeButton
                              eventId={event.id}
                              initialLiked={event.isLikedByCurrentUser}
                              initialCount={event.likeCount}
                              size="sm"
                            />
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#e10600]/30 transition-colors">
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#e10600]" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        {/* 2행: RoutePreview + 메타 정보 (모바일에서만) */}
                        <div className="flex sm:hidden items-center gap-3">
                          {event.routePreview && (
                            <div className="shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={48}
                                height={36}
                                strokeColor={event.type === 'fit_replay' ? '#0082FA' : '#e10600'}
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-xs text-white/60 flex-wrap">
                            <span>{formatDate(event.finishedAt)}</span>
                            <span>{event.riderCount || 0} {t('home.riders')}</span>
                            {(event.course?.distance ?? event.totalDistance) != null && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                              </span>
                            )}
                            {event.startedAt && event.finishedAt && (
                              <span>{formatDuration(event.startedAt, event.finishedAt)}</span>
                            )}
                            {getLocationName(event) && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <span className="truncate max-w-24">{getLocationName(event)}</span>
                              </span>
                            )}
                            {(event.viewCount ?? 0) > 0 && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                {event.viewCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Past Events */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-2xl font-bold text-white">{t('home.replays')}</h3>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-10 h-10 border-4 border-[#e10600] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : finishedEvents.length === 0 ? (
              <div className="rounded-2xl border border-white/20 bg-[#1a1a1a]/60 p-12 text-center">
                <p className="text-white/60">{t('home.noReplays')}</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {finishedEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/event/${event.id}`}
                    className="group relative rounded-xl overflow-hidden text-left transition-all hover:scale-[1.01]"
                  >
                    <div className="absolute inset-0 bg-[#1a1a1a]/70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent group-hover:from-white/15 transition-colors" />
                    <div className="absolute inset-0 border border-white/20 rounded-xl group-hover:border-white/30 transition-colors" />
                    <div className="relative p-4 sm:p-5 pl-5 sm:pl-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        {/* 1행: 제목 + 버튼 */}
                        <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4 flex-1 min-w-0">
                          {/* RoutePreview - 데스크탑에서만 이 위치 */}
                          {event.routePreview && (
                            <div className="hidden sm:block shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={56}
                                height={42}
                                strokeColor={event.type === 'fit_replay' ? '#0082FA' : '#00d2be'}
                              />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                              <span className="text-base sm:text-lg font-bold text-white truncate">{event.name}</span>
                              {event.type === 'fit_replay' ? (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#0082FA]/20 text-[#0082FA] text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
                                  FIT
                                </span>
                              ) : (
                                <span className="px-1.5 sm:px-2 py-0.5 rounded bg-[#00d2be]/20 text-[#00d2be] text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
                                  REPLAY
                                </span>
                              )}
                            </div>
                            {/* 메타 정보 - 데스크탑에서만 이 위치 */}
                            <div className="hidden sm:flex items-center gap-4 text-sm text-white/60">
                              <span>{formatDate(event.finishedAt)}</span>
                              <span>{event.riderCount || 0} {t('home.riders')}</span>
                              {(event.course?.distance ?? event.totalDistance) != null && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                  </svg>
                                  {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                                </span>
                              )}
                              {event.startedAt && event.finishedAt && (
                                <span>{formatDuration(event.startedAt, event.finishedAt)}</span>
                              )}
                              {getLocationName(event) && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate max-w-32">{getLocationName(event)}</span>
                                </span>
                              )}
                              {(event.viewCount ?? 0) > 0 && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  {event.viewCount}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* 버튼들 */}
                          <div className="flex items-center gap-2 shrink-0">
                            <LikeButton
                              eventId={event.id}
                              initialLiked={event.isLikedByCurrentUser || false}
                              initialCount={event.likeCount || 0}
                              size="sm"
                            />
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#00d2be]/30 transition-colors">
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#00d2be]" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        {/* 2행: RoutePreview + 메타 정보 (모바일에서만) */}
                        <div className="flex sm:hidden items-center gap-3">
                          {event.routePreview && (
                            <div className="shrink-0 rounded-lg bg-white/5 p-1">
                              <RoutePreviewSvg
                                preview={event.routePreview}
                                width={48}
                                height={36}
                                strokeColor={event.type === 'fit_replay' ? '#0082FA' : '#00d2be'}
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-xs text-white/60 flex-wrap">
                            <span>{formatDate(event.finishedAt)}</span>
                            <span>{event.riderCount || 0} {t('home.riders')}</span>
                            {(event.course?.distance ?? event.totalDistance) != null && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                {(event.course?.distance ?? event.totalDistance)!.toFixed(1)} km
                              </span>
                            )}
                            {event.startedAt && event.finishedAt && (
                              <span>{formatDuration(event.startedAt, event.finishedAt)}</span>
                            )}
                            {getLocationName(event) && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <span className="truncate max-w-24">{getLocationName(event)}</span>
                              </span>
                            )}
                            {(event.viewCount ?? 0) > 0 && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                {event.viewCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/20 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-white/50">
            <span className="font-bold text-white/70 font-display">PELO1</span>
            <span>© 2025 <a href="https://baselabs.co.kr" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">BaseLabs Corp.</a></span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6 text-sm">
            <Link to={i18n.language?.startsWith('ko') ? '/terms/ko' : '/terms'} className="text-white/60 hover:text-white/80 transition-colors">{t('home.terms')}</Link>
            <Link to={i18n.language?.startsWith('ko') ? '/privacy/ko' : '/privacy'} className="text-white/60 hover:text-white/80 transition-colors">{t('home.privacy')}</Link>
            <Link to="/contact" className="text-white/60 hover:text-white/80 transition-colors">{t('home.contact')}</Link>
            <div className="h-4 w-px bg-white/20" />
            <LanguageSwitcher />
          </div>
        </div>
      </footer>

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(event) => {
            setShowCreateModal(false);
            loadEvents();
            navigate(`/event/${event.id}`);
          }}
        />
      )}

      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <ComingSoonModal onClose={() => setShowComingSoonModal(false)} />
      )}

      {/* Open Event Login Required Modal */}
      {showOpenEventLoginModal && (
        <LoginRequiredModal
          isOpen={showOpenEventLoginModal}
          onClose={() => setShowOpenEventLoginModal(false)}
          onLogin={() => navigate('/login')}
          message={t('openEvent.loginRequired')}
        />
      )}
    </div>
  );
}

function CreateEventModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (event: Event) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [maxRiders, setMaxRiders] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    setIsLoading(true);
    try {
      const event = await createEvent({ name: name.trim(), maxRiders });
      onCreated(event);
    } catch (error) {
      console.error('Failed to create event:', error);
      alert(t('event.failedToCreate'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#e10600]/20 to-transparent" />
          <div className="absolute inset-0 border border-[#e10600]/30 rounded-2xl" />
          <div className="relative bg-[#0a0a0a]/90 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">{t('event.createEvent')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label className="block text-sm font-medium text-white/60 mb-2">{t('event.eventName')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('event.eventNamePlaceholder')}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-white/30 focus:border-[#e10600] focus:outline-none transition-colors"
                />
              </div>
              <div className="mb-8">
                <label className="block text-sm font-medium text-white/60 mb-2">{t('event.maxRiders')}</label>
                <input
                  type="number"
                  value={maxRiders}
                  onChange={(e) => setMaxRiders(parseInt(e.target.value) || 10)}
                  min={2}
                  max={50}
                  className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white focus:border-[#e10600] focus:outline-none transition-colors"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 rounded-lg font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !name.trim()}
                  className="flex-1 px-4 py-3 rounded-lg font-bold text-white bg-gradient-to-r from-[#e10600] to-[#ff8700] disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                >
                  {isLoading ? t('event.creating') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComingSoonModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm mx-4">
        <div className="rounded-2xl bg-[#141414] border border-white/10 p-6 text-center">
          {/* Icon */}
          <div className="w-14 h-14 rounded-full bg-[#e10600]/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#e10600]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white mb-2">{t('home.comingSoon')}</h2>

          {/* Message */}
          <p className="text-sm text-white/60 mb-6">
            {t('home.liveEventComingSoon')}
          </p>

          {/* Action */}
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 hover:bg-white/15 transition-colors"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
